import { Tag as TagType } from '@payaca/types/tagTypes';
import { FC, useCallback, useEffect, useMemo, useState, type JSX } from 'react';
import { InputStyleVariant } from '../inputWrapper/InputWrapper';
import Tag from '../tag/Tag';

import { VALID_TAG_CHARACTERS_REGEX } from '@payaca/constants/regexConstants';
import {
  getLengthFieldValidator,
  getRegexMatchFieldValidator,
} from '@payaca/helpers/fieldValidationHelper';
import { validateForm } from '@payaca/helpers/formValidationHelper';
import TypeToSearchField from '../typeToSearchField/TypeToSearchField';
import ValidatedFieldWrapper from '../validatedFieldWrapper/ValidatedFieldWrapper';

const tagLengthFieldValidator = getLengthFieldValidator(
  { min: 1, max: 30 },
  { customErrorMessage: 'Max 30 chars' }
);

const allowedCharactersFieldValidator = getRegexMatchFieldValidator(
  VALID_TAG_CHARACTERS_REGEX,
  { customErrorMessage: 'Invalid tag' }
);

type Props = {
  availableTags: TagType[];
  onAddAvailableTag: (tagId: number) => void;
  onAddNewTag?: (tagText: string) => void;
  onBlur?: () => void;
  userCanCreateNewTag: boolean;
};
const AddTagControl: FC<Props> = ({
  availableTags,
  onAddAvailableTag,
  onAddNewTag,
  onBlur,
  userCanCreateNewTag,
}: Props): JSX.Element => {
  const [tagText, setTagText] = useState('');
  const [requiresAddNewTag, setRequiresAddNewTag] = useState(false);

  const trimmedTagtext = useMemo(() => {
    return tagText.trim();
  }, [tagText]);

  const tagTextValidationResult = useMemo(() => {
    return validateForm(
      { tagText: trimmedTagtext },
      {
        tagText: [tagLengthFieldValidator, allowedCharactersFieldValidator],
      }
    ).tagText;
  }, [trimmedTagtext]);

  const options = useMemo(() => {
    return availableTags
      .sort((a: TagType, b: TagType) => {
        const textA = b.tagText.toUpperCase();
        const textB = a.tagText.toUpperCase();
        return textA < textB ? -1 : textA > textB ? 1 : 0;
      })
      .map((tag: TagType) => {
        return {
          label: tag.tagText,
          value: tag.id,
          colour: tag.tagColour,
        };
      });
  }, [availableTags]);

  const handleAddNewTag = useCallback(() => {
    const availableTag = availableTags.find(
      (x) => x.tagText === trimmedTagtext
    );
    if (availableTag) {
      onAddAvailableTag(availableTag.id);
    } else {
      if (userCanCreateNewTag) {
        onAddNewTag?.(trimmedTagtext);
      }
    }
  // oxlint-disable-next-line eslint-plugin-react-hooks(exhaustive-deps) -- FIXME: pre-existing error: React Hook useCallback has a missing dependency: 'userCanCreateNewTag'
  }, [trimmedTagtext, onAddNewTag, availableTags, onAddAvailableTag]);

  useEffect(() => {
    if (requiresAddNewTag) {
      handleAddNewTag();
      setRequiresAddNewTag(false);
    }
  // oxlint-disable-next-line eslint-plugin-react-hooks(exhaustive-deps) -- FIXME: pre-existing error: React Hook useEffect has a missing dependency: 'handleAddNewTag'
  }, [requiresAddNewTag]);

  return (
    <div className="add-tag-control">
      <ValidatedFieldWrapper
        isTouched={!!tagText?.length}
        validationResult={tagTextValidationResult}
      >
        <TypeToSearchField
          styleVariant={InputStyleVariant.STANDARD}
          value={tagText}
          options={options}
          onSelectOption={(option) => {
            if (option) {
              const tag = availableTags.find((x) => x.id == option.value);
              if (tag) {
                setTagText(tag.tagText);
                onAddAvailableTag(tag.id);
              }
            }
          }}
          onSearchTermChange={setTagText}
          changeTimeoutMs={3000}
          onEnter={() => {
            if (tagTextValidationResult.isValid) {
              setRequiresAddNewTag(true);
            }
          }}
          placeholder={'Add a tag'}
          autofocus={true}
          onBlur={onBlur}
          renderOption={(option, props) => {
            return (
              <li {...props}>
                <Tag colour={option.colour} showTagIcon={false}>
                  {option.label}
                </Tag>
              </li>
            );
          }}
          dynamicPopperWidth
        />
      </ValidatedFieldWrapper>
    </div>
  );
};

export default AddTagControl;
